import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';

import { FileVersionCommentModel } from '@files/attachments-note/attachments-note/attachments-note.model';
import { KeyValueType } from '@models/common';
import { ListResponse } from '@models/helpers';
import { ActivityDetailsItem, DataListType, FormFieldValueType } from '@models/module/fields/main';
import { EntityInstanceTransition, MultipleEntityInstanceTransition } from '@module-instance/dynamic-module/common';
import {
  AccessLogModel,
  DocumentRelationsModel,
  EntityInstanceSigners,
} from '@module-instance/dynamic-module/document-details/common';
import {
  ProgressPaymentTabInstance,
  ProgressPaymentTabRow,
} from '@module-instance/dynamic-module/document-details/common/models/progress-payment-tab.models';
import { TableDataService } from '@shared/components/data-list/common';

import {
  AssignmentEntityInstanceResponse,
  BackgroundDownloadInstancesRequestParams,
  FieldsEntityInstanceResponse,
  GetInstancesAsPdfRequestParams,
  GetInstancesAsSeparatePdfsRequestParams,
  HeaderEntityInstancesResponse,
  ModuleInstanceRequestParams,
  SaveTransitionScreenBody,
} from '@models/api';
import { EIActivityActionInstances } from '@models/module/entityInstance';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { ActivityDetailsModel } from '@module-instance/activity/common';
import { HttpService } from '@shared/services';
import { HYBRID_MODULE_ROUTE_PARTS } from './hybrid-api.model';

@Injectable({
  providedIn: 'root',
})
export class ModuleInstanceService {
  private http = inject(HttpService);
  private tableDataService = inject(TableDataService);

  private getHybridURL(moduleId: number, urlPart: string): string {
    const controller = HYBRID_MODULE_ROUTE_PARTS[moduleId] || 'ModuleInstance';
    return `_api/${controller}/${urlPart}`;
  }

  getAttachmentsNote({ entityInstanceId, fieldId, parentEntityInstanceId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, fieldId, parentEntityInstanceId });
    return this.http.get<FileVersionCommentModel[]>(`_api/ModuleInstance/GetEntityInstanceFieldAttchments`, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  saveAttachmentNote({ body, parentEntityInstanceId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ parentEntityInstanceId });
    return this.http.put<void>(`_api/ModuleInstance/SaveEntityInstanceAttachmentNote`, body, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getWorkflowId({ moduleId, entityInstanceId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, moduleId });
    return this.http.get<number>('_api/ModuleInstance/GetWorkflowId', { httpOptions: { params }, useLoader: false });
  }

  getTransition({ entityInstanceId, contractId, workflowId, moduleId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId, workflowId });
    return this.http.get<EntityInstanceTransition[]>('_api/ModuleInstance/GetTransitionsEntityInstance', {
      useLoader: false,
      httpOptions: { params, headers: { moduleId: moduleId.toString() } },
    });
  }

  getMultipleTransitions({
    entityInstanceIds,
    contractId,
    workflowId,
    moduleId,
    onlyUsedInMultiple,
  }: ModuleInstanceRequestParams & { entityInstanceIds: number[]; onlyUsedInMultiple: boolean }) {
    const data = { entityInstanceIds, contractId, workflowId, onlyUsedInMultiple };
    return this.http.post<MultipleEntityInstanceTransition[]>(
      '_api/ModuleInstance/GetTransitionsEntityInstances',
      data,
      {
        useLoader: false,
        httpOptions: { headers: { moduleId: moduleId.toString() } },
      },
    );
  }

  // TODO: This should be removed, and we should use the 'deleteMultiple' method instead
  deleteSingle({ contractId, moduleId, entityInstanceId }: ModuleInstanceRequestParams) {
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    const params = this.http.buildHttpParams({ contractId, moduleId, entityInstanceId });
    return this.http.put(
      this.getHybridURL(moduleId, 'Delete'),
      {},
      {
        httpOptions: { params, headers },
        useLoader: false,
      },
    );
  }

  deleteMultiple({ entityId, moduleId, ids }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityId, moduleId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.put('_api/ModuleInstance/DeleteMultiple', ids, {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  getAsExcel({
    contractId,
    moduleId,
    body,
    dataListType,
  }: ModuleInstanceRequestParams & { dataListType?: DataListType }) {
    const params = this.http.buildHttpParams({ contractId, moduleId, dataListType });
    return this.http.post<string>('_api/ModuleInstance/GetAsExcel', body, { httpOptions: { params } });
  }

  undoLastAction({ contractId, ids, moduleId, notifyUsers }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ contractId });
    return this.http.put(
      this.getHybridURL(moduleId, 'RollbackLastTransitionBulk'),
      { entityInstanceIds: ids, notifyUsers: notifyUsers },
      {
        httpOptions: { params },
        useLoader: false,
      },
    );
  }

  rollbackLastTransition({ actionInstanceId, contractId, moduleId, notifyUsers }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ actionInstanceId, contractId });
    return this.http.put(this.getHybridURL(moduleId, 'RollbackLastTransition'), notifyUsers, {
      httpOptions: { params, headers: { moduleId: moduleId.toString() } },
      useLoader: false,
    });
  }

  getMultipleHeaderInfo({ ids, contractId, moduleId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ contractId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<HeaderEntityInstancesResponse[]>('_api/ModuleInstance/GetHeaderEntityInstances', ids, {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  getRelations({ entityInstanceId, contractId, moduleId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.get<{ relations: DocumentRelationsModel[] }>(
      this.getHybridURL(moduleId, 'GetActivitySummaryEntityInstance'),
      {
        httpOptions: { params, headers },
        useLoader: false,
      },
    );
  }

  getAccessLog({ entityInstanceId, contractId, body }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId });
    return this.http.post<ListResponse<AccessLogModel>>(`_api/ModuleInstance/GetAccessLogEntityInstance`, body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getActivityItemDetails(
    { actionInstanceId, contractId, moduleId }: ModuleInstanceRequestParams,
    noFields: boolean = false,
  ) {
    const params = this.http.buildHttpParams({ actionInstanceId, contractId, noFields });
    return this.http.get<ActivityDetailsItem>(this.getHybridURL(moduleId, 'GetTransitionInstanceInformations'), {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getEntityInstanceSigners({ entityInstanceId, contractId, moduleId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId });
    return this.http
      .get<EntityInstanceSigners>(this.getHybridURL(moduleId, 'GetSignersEntityInstance'), {
        httpOptions: { params },
        useLoader: false,
      })
      .pipe(map(signers => signers.signers));
  }

  getModuleRoles({ moduleId, contractId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ moduleId, contractId });
    return this.http.get<KeyValueType<number>[]>('_api/ModuleInstance/ModuleRolesDict', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  syncExcludedRoles({ moduleId, contractId, body, actionInstanceId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ moduleId, contractId, actionInstanceId });
    return this.http.put<void>('_api/ModuleInstance/SyncExcludedRoles', body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getEntityInstanceProgressPayments({ parentEntityInstanceId, contractId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ parentEntityInstanceId, contractId });
    return this.http
      .post<ListResponse<ProgressPaymentTabInstance>>(
        '_api/ModuleInstance/GetEntityInstanceProgressPayments',
        { page: 1 },
        {
          httpOptions: { params, headers: { moduleId: SystemModulesEnum.ProgressPayment.toString() } },
          useLoader: false,
        },
      )
      .pipe(map(({ data }) => this.tableDataService.mapTableRowsState(data) as ProgressPaymentTabRow[]));
  }

  setPreferredValue({ actionInstanceId, contractId, actionId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ actionInstanceId, contractId, actionId });
    return this.http.put<void>(
      '_api/ModuleInstance/SetPreferredValue',
      {},
      { httpOptions: { params }, useLoader: false },
    );
  }

  getModuleRolesDict({ moduleId, contractId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ moduleId, contractId });
    return this.http.get<KeyValueType[]>('_api/ModuleInstance/ModuleRolesDict', {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getActivityEntityInstance({ moduleId, entityInstanceId, contractId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId });
    return this.http.get<EIActivityActionInstances>('_api/ModuleInstance/GetActivityEntityInstance', {
      httpOptions: { headers: { moduleId: moduleId.toString() }, params },
      useLoader: false,
    });
  }

  getTransitionInstanceInformations({ moduleId, actionInstanceId, contractId, noFields }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ actionInstanceId, contractId, noFields });
    return this.http.get<ActivityDetailsModel>(this.getHybridURL(moduleId, 'GetTransitionInstanceInformations'), {
      httpOptions: { headers: { moduleId: moduleId.toString() }, params },
      useLoader: false,
    });
  }

  /** Returns the list of full_doc_num which have empty required fields */
  checkRequiredFieldsSaveMultipleInstances({ ids, contractId, transitionId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ contractId, transitionId });
    return this.http.post<string[]>('_api/ModuleInstance/CheckRequiredFieldsSaveMultipleInstances', ids, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  checkSignTransition(transitionId: number) {
    const params = this.http.buildHttpParams({ transitionId });
    return this.http.get<boolean>('_api/ModuleInstance/CheckSignTransition', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getAssignmentEntityInstance({ moduleId, entityInstanceId, contractId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId });
    return this.http.get<AssignmentEntityInstanceResponse>('_api/ModuleInstance/GetAssignmentEntityInstance', {
      httpOptions: { headers: { moduleId: moduleId.toString() }, params },
      useLoader: false,
    });
  }

  /* This endpoint also return minor, but it's unused.
     We should update the enpoint to return KeyValueType<number>[] only
  */
  getAvailableDocumentNumberDict({ moduleId, contractId, entityInstanceId }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ moduleId, contractId, entityInstanceId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http
      .get<(KeyValueType<number> & { number: number; isPreset: boolean })[]>(
        '_api/ModuleInstance/AvailableDocumentNumberDict',
        {
          httpOptions: { params, headers },
          useLoader: false,
        },
      )
      .pipe(map(numbers => numbers.map(({ value, number, isPreset }) => ({ key: number, value, isPreset }))));
  }

  updateDocumentNumber({ entityInstanceId, documentNumber }: ModuleInstanceRequestParams) {
    return this.http.put<void>(
      '_api/ModuleInstance/UpdateDocumentNumber',
      { entityInstanceId, documentNumber },
      { useLoader: false },
    );
  }

  updateFieldValuesDocument({ moduleId, entityInstanceId, contractId, body }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId, moduleId });
    return this.http.put<void>(this.getHybridURL(moduleId, 'UpdateFieldValuesDocument'), body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  /**
   *
   * @param params
   * @returns entityInstanceId of the saved draft
   */
  saveDraftInstance({ moduleId, workflowId, entityInstanceId, contractId, body }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({
      entityInstanceId,
      contractId,
      moduleId,
      workflowId,
      transitionId: 0,
    });
    return this.http.post<number>(this.getHybridURL(moduleId, 'SaveDraftInstance'), body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  saveTemplateInstance({
    moduleId,
    workflowId,
    entityInstanceId,
    contractId,
    body,
    transitionId,
    roleId,
    presetId,
  }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({
      entityInstanceId,
      contractId,
      moduleId,
      workflowId,
      transitionId,
      roleId,
      presetId,
    });
    return this.http.post<number>(this.getHybridURL(moduleId, 'SaveTemplateInstance'), body, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getFieldsEntityInstance({
    moduleId,
    entityInstanceId,
    contractId,
    workflowId,
    transitionId,
    actionId,
    screenId,
    editMode,
    draftValues,
  }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({
      entityInstanceId,
      contractId,
      workflowId,
      transitionId,
      actionId,
      screenId,
      editMode,
    });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<FieldsEntityInstanceResponse>(
      this.getHybridURL(moduleId, 'GetFieldsEntityInstance'),
      draftValues,
      {
        httpOptions: { params, headers },
        useLoader: false,
      },
    );
  }

  getFieldsEntityInstanceToDuplicate({
    moduleId,
    entityInstanceId,
    contractId,
    workflowId,
    transitionId,
    actionId,
    screenId,
    editMode,
  }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({
      entityInstanceId,
      contractId,
      workflowId,
      transitionId,
      actionId,
      screenId,
      editMode,
    });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.get<FieldsEntityInstanceResponse>('_api/ModuleInstance/GetFieldsEntityInstanceToDuplicate', {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  getFormulaFieldValue({ contractId, fieldId, body }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ contractId, fieldId });
    return this.http.put<KeyValueType<number, FormFieldValueType>>('_api/ModuleInstance/GetFormulaFieldValue', body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  addComment(
    params: Pick<
      ModuleInstanceRequestParams,
      'entityInstanceId' | 'contractId' | 'moduleId' | 'workflowId' | 'roleId' | 'actionId'
    >,
    body?: Partial<SaveTransitionScreenBody>,
  ) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.post<void>('_api/ModuleInstance/AddComment', body, {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }

  sendActionEmail(
    params: Pick<
      ModuleInstanceRequestParams,
      'entityInstanceId' | 'contractId' | 'moduleId' | 'workflowId' | 'roleId' | 'actionId'
    >,
    body: Partial<SaveTransitionScreenBody>,
  ) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.put<void>('_api/ModuleInstance/SendActionEmail', body, {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }

  /** Create a revision.
   * @param params
   * @returns entityInstanceId of the revised document
   */
  saveReviseInstance({
    screenId,
    ...params
  }: Pick<
    ModuleInstanceRequestParams,
    'entityInstanceId' | 'contractId' | 'moduleId' | 'workflowId' | 'screenId' | 'roleId' | 'transitionId'
  >) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.post<number>(
      this.getHybridURL(params.moduleId, 'SaveReviseInstance'),
      { screenId },
      {
        httpOptions: { params: httpParams },
        useLoader: false,
      },
    );
  }

  updateFieldValuesDocumentOnRelatedDocument({
    body,
    ...params
  }: Pick<
    ModuleInstanceRequestParams<{ currentFieldId: number; entityInstanceId: number }>,
    'moduleId' | 'entityInstanceId' | 'contractId' | 'body'
  >) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.put<void>('_api/ModuleInstance/UpdateFieldValuesDocumentOnRelatedDocument', body, {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }

  getAttachmentsSize({ contractId, body }: ModuleInstanceRequestParams) {
    const params = this.http.buildHttpParams({ contractId });
    return this.http.post<number>('_api/ModuleInstance/GetAttachementsSize', body, {
      httpOptions: { params },
      useLoader: true,
    });
  }

  backgroundDownloadInstances({
    contractId,
    body,
    title,
    templateId,
    isIncludeActivity,
    isIncludeDocumentAttachments,
    isIncludeActivityAttachments,
  }: BackgroundDownloadInstancesRequestParams) {
    const params = this.http.buildHttpParams({
      contractId,
      title,
      templateId,
      isIncludeActivity,
      isIncludeDocumentAttachments,
      isIncludeActivityAttachments,
    });
    return this.http.post<void>('_api/ModuleInstance/BackgroundDownloadInstances', body, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getInstancesAsSeparatePdfs({
    contractId,
    moduleId,
    body,
    templateId,
    isIncludeActivity,
    isIncludeDocumentAttachments,
    isIncludeActivityAttachments,
    isBundleDocuments,
  }: GetInstancesAsSeparatePdfsRequestParams) {
    const params = this.http.buildHttpParams({
      contractId,
      moduleId,
      templateId,
      isIncludeActivity,
      isIncludeDocumentAttachments,
      isIncludeActivityAttachments,
      isBundleDocuments,
    });
    return this.http.post<string[]>('_api/ModuleInstance/GetInstancesAsSeparatePDFs', body, {
      httpOptions: { params },
      useLoader: true,
    });
  }

  getInstancesAsPdf({
    contractId,
    moduleId,
    body,
    title,
    templateId,
    isIncludeActivity,
    isIncludeDocumentAttachments,
    isIncludeActivityAttachments,
    isBundleDocuments,
  }: GetInstancesAsPdfRequestParams) {
    const params = this.http.buildHttpParams({
      contractId,
      moduleId,
      title,
      templateId,
      isIncludeActivity,
      isIncludeDocumentAttachments,
      isIncludeActivityAttachments,
      isBundleDocuments,
    });
    return this.http.post<string[]>('_api/ModuleInstance/GetInstancesAsPDF', body, {
      httpOptions: { params },
      useLoader: true,
    });
  }

  taggingUsersDict(params: Pick<ModuleInstanceRequestParams, 'contractId' | 'moduleId'>) {
    const { contractId, moduleId } = params;
    const httpParams = this.http.buildHttpParams({ contractId });
    return this.http.get<KeyValueType[]>('_api/ModuleInstance/TaggingUsersDict', {
      httpOptions: { params: httpParams, headers: { moduleId: moduleId?.toString() } },
      useLoader: false,
    });
  }

  /** Triggers regeneration of the final PDF for the given entity instance. */
  regeneratePdf(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId });
    return this.http.put<void>('_api/ModuleInstance/RecreatePdfOnBlob', null, {
      useLoader: true,
      httpOptions: { params },
    });
  }

  /** Triggers bulk PDF regeneration for entity instances matching the given filters. */
  populatePdfsOnBlob(filters: {
    moduleId?: number;
    contractId?: number;
    updatedOn?: string;
    fromCreatedOn?: string;
    untilCreatedOn?: string;
    fromApprovedOn?: string;
    untilApprovedOn?: string;
  }) {
    const params = this.http.buildHttpParams(filters);
    return this.http.put<void>('_api/ModuleInstance/PopulatePdfsOnBlob', null, {
      useLoader: true,
      httpOptions: { params },
    });
  }
}

export interface PdfRegenerationResult {
  total: number;
  succeeded: number;
  failed: number;
  succeededIds: number[];
  failedIds: number[];
}

export interface PdfRegenerationProgress {
  total: number;
  processed: number;
  percent: number;
}
